import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, DestroyRef, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PrefixTemplateDirective, SuffixTemplateDirective, TextBoxComponent } from '@progress/kendo-angular-inputs';
import { PopupComponent } from '@progress/kendo-angular-popup';
import { DatePipe } from '@progress/kendo-angular-intl';
import {
  catchError,
  debounce,
  distinctUntilChanged,
  isObservable,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  timer,
} from 'rxjs';
import { SearchHit, SearchHitType } from '../../models';
import { SearchService } from '../../services/features/search/search.service';
import { CapitalizePipe } from '../../pipes';

/** A query needs this many characters before it is searched. */
export const SEARCH_MIN_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;
/** Without a user setting, as many hits as a page of a list. */
const DEFAULT_ITEMS_PER_PAGE = 10;

/** The page of a hit as router commands, or null when there is none to open. */
export type SearchHitRoute = (string | number)[] | null;

/** How an app shows and opens hits of one type. */
export interface SearchHitTypeConfig {
  /** Translation key of the type, shown next to the hit. */
  label: string;
  icon: string;
  /** The page of the hit; an observable when it has to be looked up first (e.g. the parent of a visit report). */
  route: (hit: SearchHit) => SearchHitRoute | Observable<SearchHitRoute>;
  /** Documents show their date; for suppliers and articles it is only when they were indexed. */
  showsDate?: boolean;
}

export type SearchHitTypeConfigs = Partial<Record<SearchHitType, SearchHitTypeConfig>>;

type SearchState =
  | { status: 'idle' }
  | { status: 'loading'; query: string }
  | { status: 'done'; query: string; hits: SearchHit[] }
  | { status: 'error'; query: string };

interface SearchRequest {
  query: string;
  everywhere: boolean;
  /** Typing waits for the user to stop; switching "search everywhere" searches right away. */
  immediate: boolean;
}

/**
 * The "search everything" box: documents, suppliers and articles in one ranked list below the box. A hit opens its
 * page. The app decides which types it can show (`hitTypes`), which it searches by default (`types`, e.g. the user's
 * preferred ones) and which "search everywhere" searches (`allTypes`). The results open in a popup below the box, or
 * right below it with `inlineResults`.
 */
@Component({
  selector: 'rc-search-box',
  standalone: true,
  imports: [
    TextBoxComponent,
    PrefixTemplateDirective,
    SuffixTemplateDirective,
    PopupComponent,
    NgTemplateOutlet,
    TranslateModule,
    CapitalizePipe,
    DatePipe,
  ],
  templateUrl: './search-box.component.html',
  styleUrl: './search-box.component.scss',
  host: {
    '[class.search-box--inline]': 'inlineResults()',
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class SearchBoxComponent {
  /** The types this app can show and open. */
  hitTypes = input.required<SearchHitTypeConfigs>();
  /** The types searched by default. */
  types = input.required<SearchHitType[]>();
  /** The types "search everywhere" searches; offered when they are more than `types`. */
  allTypes = input<SearchHitType[]>([]);
  itemsPerPage = input<number | null | undefined>(DEFAULT_ITEMS_PER_PAGE);
  /** Translation key (or text) of the placeholder; by default the types every app searches. */
  placeholder = input('SEARCH_BOX_PLACEHOLDER');
  /** Classes of the text box, to fit the bar it is in. */
  boxClass = input('pt-8 pb-8 ps-16 pe-16 bgc-neutral-200 w-100');
  /** The results right below the box, in the page, instead of in a popup over it (e.g. in a search dialog). */
  inlineResults = input(false);
  /** A hit was opened, e.g. to close a mobile search panel. */
  hitOpened = output<void>();

  #searchService = inject(SearchService);
  #router = inject(Router);
  #host = inject(ElementRef<HTMLElement>);
  #destroyRef = inject(DestroyRef);

  private readonly textbox = viewChild.required(TextBoxComponent);
  private readonly popup = viewChild(PopupComponent, { read: ElementRef });

  protected readonly query = signal('');
  protected readonly isOpen = signal(false);
  protected readonly activeIndex = signal(0);
  protected readonly state = signal<SearchState>({ status: 'idle' });
  /** Searching all types instead of the default ones, until the query is cleared. */
  protected readonly everywhere = signal(false);
  protected readonly hits = computed(() => {
    const state = this.state();
    return state.status === 'done' ? state.hits : [];
  });
  /** "Search everywhere" only makes sense when the default types leave some out. */
  protected readonly canSearchEverywhere = computed(() => {
    const types = new Set(this.types());
    return this.allTypes().some((type) => !types.has(type));
  });
  protected readonly anchor = computed(() => this.textbox().hostElement);
  /** The results list is as wide as the search box. */
  protected readonly popupWidth = signal(0);

  #requests = new Subject<SearchRequest>();

  constructor() {
    this.#requests
      .pipe(
        map((request) => ({ ...request, query: request.query.trim() })),
        debounce((request) => timer(request.immediate ? 0 : SEARCH_DEBOUNCE_MS)),
        distinctUntilChanged((a, b) => a.query === b.query && a.everywhere === b.everywhere),
        switchMap(({ query, everywhere }) => {
          if (query.length < SEARCH_MIN_LENGTH) return of<SearchState>({ status: 'idle' });
          const types = everywhere ? this.allTypes() : this.types();
          const itemsPerPage = this.itemsPerPage() || DEFAULT_ITEMS_PER_PAGE;
          return this.#searchService.search(query, { types, itemsPerPage }).pipe(
            map((result): SearchState => ({
              status: 'done',
              query,
              // A type this app can't open can't be clicked, so it is left out
              hits: result['hydra:member'].filter((hit) => !!this.hitTypes()[hit.type]),
            })),
            catchError(() => of<SearchState>({ status: 'error', query })),
            startWith<SearchState>({ status: 'loading', query }),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe((state) => {
        this.state.set(state);
        this.activeIndex.set(0);
      });
  }

  focus() {
    this.textbox().focus();
  }

  onQueryChange(query: string) {
    this.query.set(query);
    // A new search starts from the default types again
    if (!query.trim()) this.everywhere.set(false);
    this.#requests.next({ query, everywhere: this.everywhere(), immediate: false });
    this.open();
  }

  toggleEverywhere() {
    this.everywhere.update((everywhere) => !everywhere);
    this.#requests.next({ query: this.query(), everywhere: this.everywhere(), immediate: true });
    this.focus();
  }

  open() {
    this.popupWidth.set(this.anchor().nativeElement.offsetWidth);
    this.isOpen.set(true);
  }

  close() {
    this.isOpen.set(false);
  }

  clear() {
    this.onQueryChange('');
    this.focus();
  }

  onKeydown(event: KeyboardEvent) {
    const hits = this.hits();
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        this.open();
        if (hits.length) {
          const step = event.key === 'ArrowDown' ? 1 : -1;
          this.activeIndex.set((this.activeIndex() + step + hits.length) % hits.length);
        }
        break;
      case 'Enter':
        if (this.isOpen() && hits[this.activeIndex()]) {
          event.preventDefault();
          this.openHit(hits[this.activeIndex()]);
        }
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  openHit(hit: SearchHit) {
    const config = this.hitTypes()[hit.type];
    if (!config) return;
    this.close();
    const route = config.route(hit);
    (isObservable(route) ? route.pipe(take(1), catchError(() => of(null))) : of(route))
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((commands) => {
        if (!commands) return;
        this.#router.navigate(commands);
        this.hitOpened.emit();
      });
  }

  onDocumentClick(event: MouseEvent) {
    const target = event.target as Node | null;
    if (!target || !this.isOpen()) return;
    const insideBox = this.#host.nativeElement.contains(target);
    const insidePopup = this.popup()?.nativeElement.contains(target);
    if (!insideBox && !insidePopup) this.close();
  }
}
